import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  INITIAL_OTA_STATE,
  getOtaProgressMessage,
  reduceOtaState,
  shouldHandleOtaEvent,
  startOtaUpdate,
} from '../utils/otaUpdate';

export function useOtaTransfer({ socket, token, deviceId, buildSketch, board, setProgress }) {
  const [otaState, setOtaState] = useState(INITIAL_OTA_STATE);
  const [otaInFlight, setOtaInFlight] = useState(false);
  const otaSessionRef = useRef(null);

  useEffect(() => {
    if (!token) return undefined;

    socket.emit('authenticate', token);

    const handleOtaStatus = (event) => {
      if (!shouldHandleOtaEvent(event, deviceId, otaSessionRef.current)) return;

      setOtaState((prev) => reduceOtaState(prev, event));

      const message = getOtaProgressMessage(event);
      if (message) setProgress(message);

      if (event.status === 'complete' || event.status === 'error') {
        setOtaInFlight(false);
      }
    };

    socket.on('otaStatus', handleOtaStatus);
    return () => socket.off('otaStatus', handleOtaStatus);
  }, [socket, token, deviceId, setProgress]);

  const prepareOTAFirmware = async () => {
    if (otaInFlight) return;

    try {
      setOtaInFlight(true);
      setProgress('Compiling firmware for OTA...');
      setOtaState({ status: 'compiling', progress: 0, sessionId: null, error: '' });

      const authToken = localStorage.getItem('token');
      const result = await startOtaUpdate({
        deviceId,
        sketchCode: buildSketch(),
        board,
        token: authToken,
        onAccepted: (data) => {
          otaSessionRef.current = data.sessionId;
          setOtaState({
            status: 'starting',
            progress: 0,
            sessionId: data.sessionId,
            error: '',
          });
        },
      });

      otaSessionRef.current = result.sessionId;
      setProgress(`OTA started (${result.totalChunks} chunks, ${result.firmwareSize} bytes).`);
    } catch (err) {
      console.error('OTA preparation error:', err);
      setOtaInFlight(false);
      otaSessionRef.current = null;

      let msg = err.message || 'Unknown error';
      if (err.response?.data) {
        if (typeof err.response.data === 'object') {
          msg = err.response.data.error || err.response.data.errors?.[0]?.msg || msg;
        } else {
          msg = err.response.data;
        }
      }

      setOtaState({ status: 'error', progress: 0, sessionId: null, error: msg });
      setProgress(`Failed: ${msg}`);
    }
  };

  const abortOtaUpdate = async () => {
    if (!otaSessionRef.current || !otaInFlight) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/devices/${deviceId}/ota-abort`,
        { sessionId: otaSessionRef.current },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setOtaInFlight(false);
      setOtaState((prev) => ({ ...prev, status: 'aborted', error: '' }));
      setProgress('OTA transfer aborted.');
      otaSessionRef.current = null;
    } catch (err) {
      console.error('OTA abort error:', err);
      setProgress(`Failed to abort OTA: ${err.response?.data?.error || err.message}`);
    }
  };

  const clearOtaState = () => {
    setOtaState(INITIAL_OTA_STATE);
  };

  return {
    otaState,
    otaInFlight,
    prepareOTAFirmware,
    abortOtaUpdate,
    clearOtaState,
  };
}